import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Wallet, Info, Loader2 } from 'lucide-react';

const API = `${window.location.origin}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

/**
 * Withdraw-request modal for ambassadors.
 *
 * Pulls /me/withdrawals to know:
 *   - available_balance (server is the source of truth — survives reloads)
 *   - min_withdrawal threshold
 *   - existing GCash details from the ambassador profile (pre-fills the form)
 *
 * On submit, the backend immediately locks the amount, so the wallet
 * balance the user sees on the dashboard decreases right away. This
 * prevents double-requesting the same money.
 */
export default function WithdrawModal({ profile, onClose, onSuccess }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    gcash_number: profile?.gcash_number || '',
    gcash_name: profile?.gcash_name || '',
    note: '',
  });

  useEffect(() => {
    axios.get(`${API}/ambassador/me/withdrawals`, { headers: authHeaders() })
      .then(r => {
        setData(r.data);
        setLoading(false);
        // Pre-fill amount = full balance (most common UX)
        if (r.data.available_balance > 0) {
          setForm(f => ({ ...f, amount: String(r.data.available_balance) }));
        }
      })
      .catch(e => {
        toast.error(e.response?.data?.detail || 'Failed to load wallet');
        setLoading(false);
      });
  }, []);

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.gcash_number || form.gcash_number.length < 11) { toast.error('Enter a valid GCash number'); return; }
    if (!form.gcash_name?.trim()) { toast.error('Enter the GCash account name'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/ambassador/me/withdrawals`, {
        amount,
        gcash_number: form.gcash_number.trim(),
        gcash_name: form.gcash_name.trim(),
        note: form.note?.trim() || '',
      }, { headers: authHeaders() });
      toast.success(`Withdrawal of ₱${amount.toLocaleString()} requested! We'll send it to your GCash within 24h.`);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Withdrawal failed');
    }
    setSubmitting(false);
  };

  const balance = data?.available_balance ?? 0;
  const min = data?.min_withdrawal ?? 500;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="withdraw-modal">
      <div className="bg-[#141414] border border-[#D4AF37]/30 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-white text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Request Withdrawal</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mx-auto" />
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="bg-[#0A0A0A] border border-white/5 p-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-sans">Available balance</p>
              <p className="text-3xl text-[#D4AF37] font-medium mt-1 font-sans">
                ₱{Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              {data?.pending_amount > 0 && (
                <p className="text-white/40 text-xs font-sans mt-1">
                  ₱{Number(data.pending_amount).toLocaleString()} already in-progress (pending review)
                </p>
              )}
            </div>

            {balance < min ? (
              <div className="bg-amber-400/5 border border-amber-400/30 p-3 flex gap-2 items-start">
                <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-xs font-sans">
                  Minimum withdrawal is ₱{Number(min).toLocaleString()}. Keep selling — you'll hit it soon!
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Amount (PHP)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-[#D4AF37] text-2xl font-light">₱</span>
                    <input type="number" min={min} max={balance} step="0.01" value={form.amount}
                      onChange={e => setForm({ ...form, amount: e.target.value })}
                      className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-white text-lg focus:border-[#D4AF37] focus:outline-none"
                      data-testid="wd-amount-input" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, amount: String(balance) })}
                      className="px-3 py-2.5 text-[10px] uppercase tracking-wider border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-sans"
                      data-testid="wd-max-btn"
                    >
                      Max
                    </button>
                  </div>
                  <p className="text-white/30 text-[11px] mt-1 font-sans">Min ₱{min.toLocaleString()} · Max ₱{Number(balance).toLocaleString()}</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">GCash Number</label>
                    <input type="tel" value={form.gcash_number}
                      onChange={e => setForm({ ...form, gcash_number: e.target.value.replace(/\D/g, '') })}
                      placeholder="09xxxxxxxxx"
                      className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#D4AF37] focus:outline-none font-mono"
                      data-testid="wd-gcash-num" />
                  </div>
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">GCash Name</label>
                    <input type="text" value={form.gcash_name}
                      onChange={e => setForm({ ...form, gcash_name: e.target.value })}
                      placeholder="Account holder name"
                      className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
                      data-testid="wd-gcash-name" />
                  </div>
                </div>

                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Note (optional)</label>
                  <input type="text" value={form.note}
                    onChange={e => setForm({ ...form, note: e.target.value })}
                    placeholder="e.g. Para sa rent ko this month"
                    className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
                    data-testid="wd-note" />
                </div>

                <div className="text-white/40 text-[11px] font-sans bg-[#0A0A0A] border border-white/5 p-3">
                  💡 Payouts go to GCash within 24 hours of approval. Make sure your name matches exactly — mismatched transfers are reversed.
                </div>

                <button
                  onClick={submit}
                  disabled={submitting || balance < min}
                  data-testid="wd-submit-btn"
                  className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.2em] font-medium hover:bg-[#E5C158] transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Requesting…' : `Request ₱${(parseFloat(form.amount) || 0).toLocaleString()}`}
                </button>
              </>
            )}

            {data?.items?.length > 0 && (
              <div className="pt-3 border-t border-white/5">
                <p className="text-white/40 text-[10px] uppercase tracking-wider font-sans mb-2">Recent activity</p>
                <ul className="space-y-1.5">
                  {data.items.slice(0, 3).map(w => (
                    <li key={w.id} className="flex items-center justify-between text-xs font-sans">
                      <div>
                        <span className="text-white/80">₱{Number(w.amount).toLocaleString()}</span>
                        <span className="text-white/30 ml-2">{new Date(w.created_at).toLocaleDateString('en-PH')}</span>
                      </div>
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 ${
                        w.status === 'paid' ? 'text-[#2E9B50] bg-[#1C7D3F]/15' :
                        w.status === 'approved' ? 'text-blue-400 bg-blue-400/15' :
                        w.status === 'rejected' ? 'text-red-400 bg-red-400/15' :
                        'text-yellow-400 bg-yellow-400/15'
                      }`}>
                        {w.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
